import { useMemo, useState } from 'react';
import InfoCard from '@/components/InfoCard';
import { ChevronRight, UserX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOnLeaveToday } from '@/hooks/dashboard';
import { useLeaveSettings } from '@/hooks/leave-management';
import {
  formatDate,
  formatTimeHHMM,
  getHalfDaySessionLabel,
  getWeekDay,
} from '@gokrew/ui/utility/utility';
import {
  NEXT_WORKING_DAY_LOOKAHEAD_DAYS,
  addDaysToKey,
  buildPresenceBuckets,
  dateFromKey,
  findNextWorkingDay,
  getNonWorkingDay,
  getOnLeaveDurationKind,
  getWorkingDayWindow,
  type WorkingDayCalendar,
} from '@gokrew/ui/utility/onLeavePresence';
import styles from './OnLeaveTodayCard.module.css';
import {
  Button,
  Chip,
  OnLeaveDaySelector,
  OnLeaveNextWorkingDay,
  OnLeavePresenceBar,
  OnLeaveRow,
} from '@gokrew/ui';
import { Tone } from '@gokrew/models/ui.types';
import { LeaveDuration } from '@gokrew/models/leaves';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { TranslationNS } from '@gokrew/models/constants';
import type { OnLeaveEmployee } from '@/types/dashboard';
import { CardLoader } from './CardLoader';
import { getLeaveHours } from './dashboardUtils';
import { DashboardEmptyState } from './DashboardEmptyState';

const WEEK_CONFIG: Intl.DateTimeFormatOptions = { weekday: 'short' };
const DEFAULT_WEEKLY_HOLIDAYS = [0, 6];

function toDateKey(value: string) {
  return value.slice(0, 10);
}

function getTagLabel(item: OnLeaveEmployee, t: TFunction) {
  switch (getOnLeaveDurationKind(item)) {
    case LeaveDuration.HOURLY:
      return t('leave_requests.dashboard_page.hours', {
        count: getLeaveHours(item.startTime!, item.endTime!),
      });
    case LeaveDuration.HALF_DAY:
      return t('leave_duration.halfDay');
    case LeaveDuration.FULL_DAY:
    default:
      return t('leave_duration.fullDay');
  }
}

function getSubtitle(item: OnLeaveEmployee, t: TFunction, locale: string) {
  const leaveType = item.leaveType?.name;
  const dateConfig = { format: 'medium' as const, locale };
  const weekday = (date: string) => getWeekDay(date, WEEK_CONFIG, locale);

  const detail = (() => {
    switch (getOnLeaveDurationKind(item)) {
      case LeaveDuration.HOURLY:
        return `${weekday(item.startDate)}, ${formatDate(item.startDate, dateConfig)} · ${formatTimeHHMM(item.startTime!)} – ${formatTimeHHMM(item.endTime!)}`;
      case LeaveDuration.HALF_DAY:
        return `${weekday(item.startDate)}, ${formatDate(item.startDate, dateConfig)} (${getHalfDaySessionLabel(item.startSession, t)})`;
      case LeaveDuration.FULL_DAY:
      default:
        // Still part of a longer request — show its whole range, not just
        // the single day being viewed.
        if (item.startDate !== item.endDate) {
          return `${weekday(item.startDate)}, ${formatDate(item.startDate, dateConfig)} – ${weekday(item.endDate)}, ${formatDate(item.endDate, dateConfig)}`;
        }
        return `${weekday(item.startDate)}, ${formatDate(item.startDate, dateConfig)}`;
    }
  })();

  return leaveType ? `${leaveType} · ${detail}` : detail;
}

export function OnLeaveTodayCard({ dataTour }: { dataTour?: string }) {
  const { t, i18n } = useTranslation(TranslationNS.LeaveManagement);
  const navigate = useNavigate();

  const { data: leaveSettingsData } = useLeaveSettings();
  const weeklyHolidays = useMemo(
    () =>
      (
        leaveSettingsData?.data?.leaveSetting?.weeklyHolidays ??
        DEFAULT_WEEKLY_HOLIDAYS
      ).map(Number),
    [leaveSettingsData]
  );

  const { dates, todayKey } = useMemo(() => {
    const { dates: window, today } = getWorkingDayWindow(weeklyHolidays);
    return {
      dates: window,
      todayKey: toDateKey(formatDate(today, { format: 'iso' })),
    };
  }, [weeklyHolidays]);

  const startDate = toDateKey(formatDate(dates[0], { format: 'iso' }));
  /* Past the strip, so a non-working last day still finds its next working
     day (and the holidays on the way) in the payload. */
  const endDate = addDaysToKey(
    toDateKey(formatDate(dates[dates.length - 1], { format: 'iso' })),
    NEXT_WORKING_DAY_LOOKAHEAD_DAYS
  );

  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);

  const { data, isLoading, isError } = useOnLeaveToday({ startDate, endDate });

  const leavesByDate = useMemo(() => data?.data.leaves ?? {}, [data]);

  const dayStrip = useMemo(
    () =>
      dates.map((date) => {
        const key = toDateKey(formatDate(date, { format: 'iso' }));
        return {
          key,
          label:
            key === todayKey
              ? t('leave_requests.dashboard_page.today')
              : date.toLocaleDateString(i18n.language, {
                  weekday: 'short',
                  day: 'numeric',
                }),
          count: (leavesByDate[key] ?? []).length,
        };
      }),
    [dates, todayKey, leavesByDate, i18n.language, t]
  );

  const activeKey = selectedDayKey ?? todayKey;

  /* The server returns the viewer's own location's holidays only. */
  const workingCalendar = useMemo<WorkingDayCalendar>(
    () => ({ weeklyHolidays, holidays: data?.data.holidays ?? [] }),
    [weeklyHolidays, data]
  );

  const nonWorkingDay = useMemo(
    () => getNonWorkingDay(activeKey, workingCalendar),
    [activeKey, workingCalendar]
  );
  const isWorkingDay = nonWorkingDay === null;

  /* Nobody is expected in on a day off, so the card shows the next working
     day's absences instead of an empty bar and zero counters. */
  const nextWorkingDayKey = useMemo(
    () =>
      nonWorkingDay ? findNextWorkingDay(activeKey, workingCalendar) : null,
    [nonWorkingDay, activeKey, workingCalendar]
  );

  const listKey = isWorkingDay ? activeKey : nextWorkingDayKey;

  const activeDayEmployees = useMemo(
    () => (listKey ? (leavesByDate[listKey] ?? []) : []),
    [leavesByDate, listKey]
  );

  const nonWorkingChip = !nonWorkingDay
    ? null
    : nonWorkingDay.kind === 'holiday'
      ? t('leave_requests.dashboard_page.holiday_chip', {
          name: nonWorkingDay.holidayName,
        })
      : nonWorkingDay.kind === 'weekend'
        ? t('leave_requests.dashboard_page.weekend_chip')
        : t('leave_requests.dashboard_page.non_working_day_chip');

  const nextWorkingDayLabel = nextWorkingDayKey
    ? t('leave_requests.dashboard_page.next_working_day', {
        date: dateFromKey(nextWorkingDayKey).toLocaleDateString(i18n.language, {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
        }),
      })
    : null;

  const totalEmployeeCount = data?.data.totalEmployeeCount ?? 0;

  const presence = useMemo(
    () =>
      buildPresenceBuckets(
        activeDayEmployees,
        totalEmployeeCount,
        getOnLeaveDurationKind,
        {
          present: t('leave_requests.dashboard_page.present'),
          hourly: t('leave_duration.hourly'),
          halfDay: t('leave_duration.halfDay'),
          fullDay: t('leave_duration.fullDay'),
        },
        { isWorkingDay }
      ),
    [activeDayEmployees, totalEmployeeCount, isWorkingDay, t]
  );

  return (
    <InfoCard
      dataTour={dataTour}
      className={styles.card}
      title={t('leave_requests.dashboard_page.on_leave')}
      action={
        <Chip
          type={isWorkingDay ? Tone.WARNING : Tone.SOFT_GRAY}
          size="sm"
          className={styles.outChip}
        >
          {nonWorkingChip ??
            t('leave_requests.dashboard_page.in_out', {
              in: presence.present,
              out: activeDayEmployees.length,
            })}
        </Chip>
      }
      subHeader={
        isWorkingDay ? <OnLeavePresenceBar presence={presence} /> : undefined
      }
      footer={
        <div className="flex flex-col gap-3">
          <OnLeaveDaySelector
            label={t('leave_requests.dashboard_page.out_by_day')}
            days={dayStrip}
            activeKey={activeKey}
            todayKey={todayKey}
            onSelect={setSelectedDayKey}
          />

          <Button
            variant="primary"
            size="sm"
            className={styles.viewCalendarButton}
            onClick={() => navigate('/leave-management/calendar')}
          >
            {t('leave_requests.dashboard_page.view_team_calendar')}
            <ChevronRight size={16} />
          </Button>
        </div>
      }
    >
      {nextWorkingDayLabel && (
        <OnLeaveNextWorkingDay label={nextWorkingDayLabel} />
      )}

      {isLoading && <CardLoader />}
      {isError && <div>{t('toast.create.something_went_wrong')}</div>}
      {!isLoading && !isError && activeDayEmployees.length === 0 && (
        <DashboardEmptyState
          layout="row"
          icon={UserX}
          title={
            isWorkingDay
              ? t('leave_requests.dashboard_page.empty_state_title')
              : t('leave_requests.dashboard_page.next_day_empty')
          }
          description={
            isWorkingDay
              ? t('leave_requests.dashboard_page.empty_state_description')
              : undefined
          }
        />
      )}

      {activeDayEmployees.length > 0 && (
        <div className={styles.employeeList}>
          {activeDayEmployees.map((item) => (
            <OnLeaveRow
              key={item.leaveRequestId}
              employee={item.employee}
              meta={getSubtitle(item, t, i18n.language)}
              kind={getOnLeaveDurationKind(item)}
              tag={getTagLabel(item, t)}
            />
          ))}
        </div>
      )}
    </InfoCard>
  );
}
